<template>
  <section class="quick-play" :class="{ 'quick-play--collapsed': collapsed }" :aria-label="$t('Quick Play')">
    <!--
      v2.1: the card is the rail's footer bar, always on screen and reporting the
      table, and the board opens as a sheet over the list above it. The bar is
      the toggle; the sheet's own close button folds it too.
    -->
    <div v-if="!collapsed" class="quick-play__sheet">
      <header class="quick-play__header">
        <span class="quick-play__id">
          <em>{{ table ? `${table.code} #${table.round}` : '--' }}</em>
        </span>

        <div class="quick-play__dice">
          <TaiXiuDiceRow v-if="table" :table="table" :is-rolling="isRolling" float-result />
        </div>

        <!-- <span v-if="showTimer" class="quick-play__timer" :class="{ closing: secondsRemaining <= 5 }">
          00:{{ paddedSeconds }}
        </span> -->
        <button
          type="button"
          class="quick-play__icon quick-play__close"
          :aria-label="$t('Close Quick Play')"
          :aria-expanded="String(!collapsed)"
          @click="collapsed = true"
        >
          <img src="@/assets/images/quick-play-close.svg" alt="" />
        </button>
      </header>

      <div class="quick-play__tables">
        <ArenaChips :items="tableItems" @select="setQuickPlayTable($event.id)" />
        <!-- <button type="button" class="quick-play__icon" :aria-label="$t('Info')">
          <img src="@/assets/svg/tx-quickplay/info.svg" alt="" />
        </button> -->
      </div>

      <TxQuickBoard
        v-if="table"
        :table="table"
        :active-chip="activeChip"
        :draft-bets="draftBets"
        :placed-bets="placedBets"
        :is-submitting="isSubmitting"
        :is-rolling="isRolling"
        :can-undo="canUndo"
        @bet="addBet"
        @select-chip="selectChip"
        @undo="undoBet"
        @clear="clearBets"
        @submit="submitBets"
      />
      <p v-else class="quick-play__empty">{{ $t('Pick a table to start') }}</p>
    </div>

    <button
      type="button"
      class="quick-play__bar"
      :aria-label="$t('Quick Play')"
      :aria-expanded="String(!collapsed)"
      @click="collapsed = !collapsed"
    >
      <img class="quick-play__bar-dice" src="@/assets/images/dice-icon-active.svg" alt="" />
      <span class="quick-play__bar-label">{{ $t('Quick Play') }}</span>
      <span class="quick-play__bar-table">{{ table ? table.code : '--' }}</span>
      <!-- Countdown hidden, as in the board header (tai-xiu-board-header.vue). -->
      <!-- <span v-if="showTimer" class="quick-play__bar-timer" :class="{ closing: secondsRemaining <= 5 }">
        00:{{ paddedSeconds }}
      </span> -->
      <!-- The whole bar is the control; the chevron is only the affordance. -->
      <span class="quick-play__expand" aria-hidden="true">
        <img src="@/assets/svg/tx-quickplay/chevron.svg" alt="" />
      </span>
    </button>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ArenaChips from '@/components/desktop/arena-chips.vue';
import { getTableStatusClass } from '@/store/modules/tai-xiu';
import TaiXiuDiceRow from '@/components/tai-xiu/tai-xiu-dice-row.vue';
import TxQuickBoard from '@/components/desktop/tx-quick-board.vue';

/*
 * The Tài Xỉu quick-play card docked under the rail -- a trimmed quick-play
 * mode, so Tài Xỉu stays playable without giving a pane over to it.
 *
 * It drives `taiXiu/quickBetTable`, deliberately separate from the table pane A
 * shows, so the rail and the pane can sit on different tables.
 *
 * Layout is the v2.1 prototype: a 54px footer bar on the rail card, with the
 * board in a sheet that opens over the list above it.
 */
export default {
  name: 'QuickPlayPanel',
  components: {
    ArenaChips,
    TaiXiuDiceRow,
    TxQuickBoard,
  },
  data() {
    return {
      // Local: nothing outside this card cares whether it is folded, and the
      // table it plays stays selected either way. Starts folded: open, the
      // sheet covers the CURRENT list (v2.1).
      collapsed: true,
    };
  },
  computed: {
    ...mapGetters('taiXiu', [
      'tables',
      'quickBetTable',
      'activeChip',
      'draftBets',
      'placedBets',
      'isSubmitting',
      'isRolling',
      'canUndo',
    ]),
    table() {
      return this.quickBetTable;
    },
    // Same chips as the pane's arena rail, so the two Tài Xỉu rails match.
    tableItems() {
      return (this.tables || []).map((table, index) => ({
        key: table.id,
        product: 'tai-xiu',
        id: table.id,
        code: table.code,
        index,
        statusClass: getTableStatusClass(table),
        active: this.isTableActive(table.id),
        disabled: table.isAvailable === false,
      }));
    },
    // Only an auto-closing arena closes betting on a timer.
    showTimer() {
      return !!this.table && this.table.autoClose;
    },
    secondsRemaining() {
      return (this.table && this.table.secondsRemaining) || 0;
    },
    paddedSeconds() {
      // Same source as the mobile board (tai-xiu-full-board-top.vue).
      return `0${this.secondsRemaining}`.slice(-2);
    },
  },
  watch: {
    /*
     * The card is always on screen on desktop, so it opens on a table rather
     * than asking for one: "Pick a table to start" is only for the case where
     * the table list itself is empty.
     */
    tables: {
      immediate: true,
      handler(tables) {
        if (this.table || !tables || !tables.length) return;
        this.setQuickPlayTable(tables[0].id);
      },
    },
    // The quick-play card can be on a different table from either pane, so it
    // asks for that table's chips too (de-duplicated in the store action).
    'table.arenaId': {
      immediate: true,
      handler(arenaId) {
        if (arenaId) this.getArenaBetChips(arenaId);
      },
    },
  },
  methods: {
    ...mapActions('taiXiu', ['setQuickPlayTable', 'selectChip', 'addBet', 'undoBet', 'clearBets', 'submitBets']),
    ...mapActions('general', ['getArenaBetChips']),
    isTableActive(tableId) {
      return !!this.table && this.table.id === tableId;
    },
  },
};
</script>

<style lang="scss" scoped>
/*
 * v2.1: the rail card's footer. The section itself is static, so the sheet
 * positions against the rail card (side-rail.vue), not against this bar.
 */
.quick-play {
  box-sizing: border-box;
}

/*
 * The 54px bar: the whole bar is the button, so the chevron inside is a plain
 * span, not a nested button.
 */
.quick-play__bar {
  position: relative;
  z-index: 6;
  width: 100%;
  height: 54px;
  padding: 0 14px;
  border: 0;
  border-top: 1px solid #19253c;
  background: $ga-rail-footer-bg;
  font: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;

  &:hover .quick-play__expand {
    border-color: #efc87f;
  }
}

.quick-play__bar-dice {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  display: block;
}

.quick-play__bar-label {
  color: #a1bbc9;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
}

.quick-play__bar-table {
  color: #6a91a7;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.quick-play__bar-timer {
  margin-left: auto;
  color: #efc87f;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  font-feature-settings: 'tnum' on, 'lnum' on;

  &.closing {
    color: #fa6f6f;
  }
}

// With no timer to push it, the chevron keeps to the right edge itself.
.quick-play__bar-table + .quick-play__expand {
  margin-left: auto;
}

.quick-play__expand {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  box-sizing: border-box;
  border: 1px solid rgba(239, 200, 127, 0.5);
  border-radius: 50%;
  background: transparent;
  display: grid;
  place-items: center;
  transition: transform 0.2s;

  img {
    width: 9px;
    height: 6px;
    display: block;
  }
}

// Open: the chevron turns to point the way the sheet folds.
.quick-play:not(.quick-play--collapsed) .quick-play__expand {
  transform: rotate(180deg);
}

/* The sheet: over the list, sitting on the bar, gold edge on top. */
.quick-play__sheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 54px;
  z-index: 5;
  box-sizing: border-box;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #030f15;
  border-top: 1px solid rgba(239, 200, 127, 0.5);
  box-shadow: 0 -14px 30px rgba(0, 0, 0, 0.6);
}

/* ID #round  (dice pill)  (x) -- 28px tall. */
.quick-play__header {
  height: 28px;
  flex: none;
  display: flex;
  align-items: center;
}

.quick-play__icon {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.quick-play__close {
  flex-basis: 20px;
  width: 20px;
  height: 20px;
}

.quick-play__id {
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;

  em {
    color: #efc87f;
    font-style: normal;
  }
}

/* The result dice, in the info bar's dark pill. */
.quick-play__dice {
  position: relative;
  flex: 0 0 76px;
  height: 24px;
  margin: 0 auto;
  display: grid;
  place-items: center;

  // v2 pill: outline and gradient at 30% opacity, the dice at full.
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border: 1.5px solid rgba(178, 214, 234, 0.8);
    border-radius: 12px;
    background: linear-gradient(180deg, rgba(22, 30, 54, 0.5) 0%, rgba(15, 21, 38, 0.4) 100%);
    opacity: 0.3;
  }

  ::v-deep .tx-dice-row {
    position: relative;

    // The total hangs off the pill's right edge rather than being squeezed in.
    .tx-dice-row__result {
      left: calc(100% + 8px);
    }
  }
}

.quick-play__timer {
  margin-right: 8px;
  color: #efc87f;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;

  &.closing {
    color: #fa6f6f;
  }
}

.quick-play__tables {
  height: 30px;
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.quick-play__empty {
  margin: 24px 0;
  color: $ga-text-muted;
  font-size: 12px;
  text-align: center;
}
</style>
